import { Card } from '../../../molecules/Card/Card';
import { isEmpty, type ChartRow } from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type CompetitorSentimentComparisonView = {
  /** `Your negative share`, `Best in category`, `Your gap to best`. */
  kpis: KPIMetric[];
  /** One row per brand, **worst negative share first**; one key per sentiment (counts). */
  rows: ChartRow[];
  /** The brand option value behind each row, for the drill-down. */
  keys: string[];
  xKey: string;
  series: ChartSeries[];
  /** Whether any mention is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
};

export type CompetitorSentimentComparisonCardProps = WidgetBlockProps & {
  view?: CompetitorSentimentComparisonView;
  /** Opens the conversations behind a bar (index into `rows`, i.e. the re-sorted order). */
  onViewDetails?: (index: number) => void;
};

/**
 * Competitors' third card (931:36991), and Overview's (972:21116).
 *
 * The sentiment mix for us alongside each competitor, normalised so market size
 * cannot dominate. Cross-brand — see `ShareOfVoiceCard` for what changes between
 * its two callers. Nothing here does: the card names no control, so it reads the
 * same on a screen with a `Brands` chip and on one without.
 *
 * Ordered by negative **share**, worst first — the one thing a normalised chart
 * is read for. A brand below a handful of mentions (`BRAND_FLOOR`) can still top
 * the chart; it cannot be named best in category.
 */
export function CompetitorSentimentComparisonCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: CompetitorSentimentComparisonCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title="Competitor sentiment comparison"
      helpText="The sentiment mix for us alongside each competitor, normalised so market size cannot dominate."
      helpSupportingText="Ordered worst first by negative share. A brand below a handful of mentions can still top the chart; it cannot be named best in category."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            onViewDetails={onViewDetails}
            xKey={view.xKey}
            series={view.series}
            stacked
            normalize
            orientation="horizontal"
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
